import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { CesiumViewer } from '~/components/cesium/CesiumViewer';
import type { ScreenSpaceEventHandler } from 'cesium';
import { Cartographic, HeadingPitchRoll } from 'cesium';
import type { TilesetParams } from '~/components/cesium/cesiumUtils';
import {
  enableEntityInfoBox,
  flyToLocation,
  removeEntity,
  TerrainProvider,
} from '~/components/cesium/cesiumUtils';
import { Heading } from '~/components/common/Heading';
import { Button } from '~/components/ui/button';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { Menu } from '~/components/ui/menu';
import * as R from 'ramda';
import { useVftPageContext, VftPageContextProvider } from '~/utils/modules/vft';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { NotFound } from '~/components/common/NotFound';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretLeft,
  faCaretRight,
  faVideoCamera,
} from '@fortawesome/free-solid-svg-icons';
import { Checkbox } from '~/components/ui/forms/Checkbox';
import type {
  CAType,
  OutcropType,
  SlideType,
  VftType,
} from '~/routes/upload/vft-manager/$vftId';
import {
  addEntity,
  ExpandableSection,
  OutcropSection,
  placementToSO,
  studyHasPlaceableContent,
  StudySection,
  VFT_PAGE_QUERY,
  VomItem,
} from '~/routes/upload/vft-manager/$vftId';
import { cn } from '~/utils/common';
import type { SO } from '~/routes/upload/model/outcrop/$outcropId/so-placements';

export default function VftViewer({ id }: { id: string }) {
  return (
    <>
      <ApolloProviderV4>
        <VftPageContextProvider>
          <VftDataFetch id={id} />
        </VftPageContextProvider>
      </ApolloProviderV4>
    </>
  );
}

function VftDataFetch({ id }: { id: string }) {
  const { data, loading } = useQuery(VFT_PAGE_QUERY, {
    variables: {
      id: id,
    },
  });

  if (loading) {
    return <SpinnerPlaceholder />;
  }
  const vft = data?.virtualFieldTripGet;

  if (!vft) {
    return <NotFound />;
  }

  return <InnerPage vft={vft} />;
}

function SlideSidebar({
  initialTilesets,
  initialCesiumAssets,
  outcrops,
}: {
  initialTilesets: TilesetParams[];
  initialCesiumAssets?: CAType[];
  outcrops: OutcropType[];
}) {
  const { slides, currentSlideId } = useVftPageContext();

  const currentSlide = slides.find(s => s.id === currentSlideId);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Menu>
          {currentSlide && (
            <SlideItem
              slide={currentSlide}
              initialTilesets={initialTilesets}
              initialCesiumAssets={initialCesiumAssets}
              outcrops={outcrops}
            />
          )}
        </Menu>
      </div>
    </div>
  );
}

function CAItem({
  ca,
  initialTilesets,
}: {
  ca: CAType;
  initialTilesets: TilesetParams[];
}) {
  const { viewer, hiddenTilesets, setHiddenTilesets } = useVftPageContext();
  const [isVisible, setIsVisible] = useState(true);
  const token = ca.tilesetToken?.token;
  const vom = ca.virtualOutcropModel;
  const camera = ca.defaultCamera;

  function handleFlyTo() {
    if (!camera || !viewer) return;
    flyToLocation(viewer, camera);
  }

  function handleToggleVisibility(token: string | null | undefined) {
    const isVisibleNow = !isVisible;
    const ts = initialTilesets.find(ts => {
      return ts.assetToken === token;
    });
    if (!ts || !token) {
      return;
    }
    if (isVisibleNow) {
      setHiddenTilesets(hiddenTilesets.filter(ts => ts.assetToken !== token));
    } else {
      setHiddenTilesets([...hiddenTilesets, ts]);
    }
    setIsVisible(isVisibleNow);
  }

  return (
    <div className="flex justify-between gap-6 w-full items-center">
      <div className="text-left flex">
        <input
          type="checkbox"
          className="checkbox checkbox-xs"
          checked={!!token && isVisible}
          onChange={() => {
            handleToggleVisibility(token);
          }}
          disabled={!token}
        />
        <div
          className={cn(
            'self-center ml-2 pl-2 border-l border-slate-200 border-dotted text-base max-w-full leading-4',
            {
              'text-slate-800': !!token,
              'text-slate-400': !token,
            },
          )}
        >
          {vom?.name}
        </div>
      </div>
      <div className="text-right">
        {token && camera && (
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={handleFlyTo}
            disabled={!token}
          >
            <FontAwesomeIcon icon={faVideoCamera} />
          </Button>
        )}
      </div>
    </div>
  );
}

function SOItem({ so }: { so: SO | null }) {
  const { viewer, showLabels } = useVftPageContext();
  const [isVisible, setIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  if (!so) {
    return;
  }
  const hasPlacement = !!so.placement;
  const soId = `${so.__typename}-${so.id}`;
  const camera = so?.placement?.defaultCamera;

  function handleToggleVisibility() {
    const newVisible = !isVisible;
    setIsVisible(newVisible);
    if (viewer && so) {
      if (newVisible) {
        addEntity(viewer, true, showLabels)(so);
      } else {
        removeEntity(viewer, soId);
      }
    }
  }

  function handleFlyTo() {
    if (!camera || !viewer) return;
    flyToLocation(viewer, camera);
  }

  return (
    <div className="flex justify-between gap-6 w-full items-center">
      <div
        className="flex justify-between gap-2 w-full items-start"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input
          type="checkbox"
          className="checkbox checkbox-xs"
          checked={!!hasPlacement && isVisible}
          onChange={handleToggleVisibility}
          disabled={!hasPlacement}
        />
        <label
          className={cn(
            'label items-start border-l-2 border-slate-200 border-dotted grow pt-1 pl-2.5',
            {
              truncate: !isHovered,
            },
          )}
        >
          <div
            className={cn('w-full', {
              truncate: !isHovered,
            })}
          >
            {so.placement?.displayName ?? so.name}
          </div>
        </label>
        <div className="text-right">
          {camera && (
            <Button
              type="button"
              color="ghost"
              size="xs"
              onClick={handleFlyTo}
              disabled={!camera}
            >
              <FontAwesomeIcon icon={faVideoCamera} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function OutcropItem({
  outcrop,
  initialTilesets,
  initialCesiumAssets,
  currentSlide,
}: {
  outcrop: OutcropType;
  initialTilesets: TilesetParams[];
  initialCesiumAssets: CAType[];
  currentSlide: SlideType;
}) {
  const outcropId = parseInt(outcrop.id);
  const studies = R.pipe(
    () => outcrop.studies,
    R.uniqBy(study => study.id),
    R.sortBy(study => study.name ?? ''),
    R.filter(studyHasPlaceableContent(outcropId)),
  )();

  const voms = outcrop.virtualOutcropModels || [];
  const slideVoms = currentSlide.cesiumAssets.map((ca) => ca.virtualOutcropModel?.id)
  const filteredVoms = voms.filter((vom) => !slideVoms.includes(vom.id));
  const sopIds = currentSlide.placements.map((p) => p.id);

  return (
    <div className="space-y-4">
      {!!voms.length && (
        <ExpandableSection heading={outcrop.name}>
          <div className="space-y-1">
            {filteredVoms.map(vom => (
              <VomItem
                key={`vom-${vom.id}`}
                vom={vom}
                initialTilesets={initialTilesets}
                initialCesiumAssets={initialCesiumAssets}
                refetchQueries={[]}
              />
            ))}
          </div>
        </ExpandableSection>
      )}

      <OutcropSection outcrop={outcrop} refetchQueries={[]} removeSopIds={sopIds} />

      {!!studies.length && (
        <ExpandableSection heading="Studies">
          <div className="space-y-2">
            {studies.map(study => (
              <StudySection
                key={study.id}
                outcropId={outcropId}
                study={study}
                refetchQueries={[]}
                removeSopIds={sopIds}
              />
            ))}
          </div>
        </ExpandableSection>
      )}
    </div>
  );
}

function SlideItem({
  slide,
  initialTilesets,
  initialCesiumAssets,
  outcrops,
}: {
  slide: SlideType;
  initialTilesets: TilesetParams[];
  initialCesiumAssets?: CAType[];
  outcrops: OutcropType[];
}) {
  const placements = slide.placements || [];

  return (
    <>
      <div className={"max-w-full"} key={`slide-${slide.id}`}>
        <Heading level={3}>Virtual Outcrop Models</Heading>
        <div>
          {slide.cesiumAssets.map(ca => {
            return (
              <CAItem
                key={`ca-${ca.id}`}
                ca={ca}
                initialTilesets={initialTilesets}
              />
            );
          })}
        </div>
        <Heading level={3}>Supporting Objects</Heading>
        <div>
          {placements.map(p => {
            const so = placementToSO(p);
            return <SOItem key={`p-${p.id}`} so={so} />;
          })}
        </div>
      </div>
      <div className={"max-w-full"} >
        <Heading level={4}>Current Outcrops</Heading>
        <div>
          {outcrops.map(outcrop => (
            <OutcropItem
              key={`outcrop-${outcrop.id}`}
              outcrop={outcrop}
              currentSlide={slide}
              initialTilesets={initialTilesets}
              initialCesiumAssets={initialCesiumAssets || []}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function InnerPage({ vft }: { vft: NonNullable<VftType> }) {
  const {
    viewer,
    setViewer,
    slides,
    setSlides,
    currentSlideId,
    setCurrentSlideId,
    hiddenTilesets,
    setHiddenTilesets,
    showLabels,
    setShowLabels
  } = useVftPageContext();
  const [infoBoxHandler, setInfoBoxHandler] =
    useState<ScreenSpaceEventHandler>();
  setShowLabels(false);

  const outcrops = useMemo(() => {
    return vft?.outcrops || [];
  }, [vft]);
  const initialSlides = useMemo(() => {
    const slides = vft?.slides || [];
    return R.sortBy(s => s.position, slides);
  }, [vft]);

  useEffect(() => {
    setSlides(initialSlides);
    if (initialSlides.length > 0) {
      setCurrentSlideId(initialSlides[0].id);
    }
  }, [initialSlides, setSlides, setCurrentSlideId]);

  const initialTilesets = useMemo(() => {
    const its: TilesetParams[] = [];
    outcrops?.forEach(oc => {
      oc.virtualOutcropModels.forEach(vom => {
        const cur = vom.cesiumAsset;
        if (
          !cur ||
          !cur.location ||
          !cur.tilesetToken ||
          !cur.tilesetUrlLocal
        ) {
          return;
        }
        its.push({
          transform: {
            location: Cartographic.fromDegrees(
              cur.location.longitude,
              cur.location.latitude,
              cur.location.height,
            ),
            orientation: new HeadingPitchRoll(
              cur.location.heading,
              cur.location.pitch,
              cur.location.roll,
            ),
          },
          assetToken: cur.tilesetToken.token,
          localPath: cur.tilesetUrlLocal,
        } satisfies TilesetParams);
      });
    });
    return its;
  }, [outcrops]);

  const initialCesiumAssets = useMemo(() => {
    const initialCesiumAssets: CAType[] = [];
    outcrops?.forEach(oc => {
      oc.virtualOutcropModels.forEach(vom => {
        const cur = vom.cesiumAsset;
        if (
          !cur ||
          !cur.location ||
          !cur.tilesetToken ||
          !cur.tilesetUrlLocal
        ) {
          return;
        }
        initialCesiumAssets.push(cur);
      });
    });
    return initialCesiumAssets;
  }, [outcrops]);

  useEffect(() => {
    // slide changed
    const currentSlide = slides.find(s => s.id === currentSlideId);
    if (!currentSlide) {
      return;
    }
    if (viewer && currentSlide.defaultCamera) {
      flyToLocation(viewer, currentSlide.defaultCamera);
    }
    const slideCAUrls = currentSlide.cesiumAssets.map(
      ca => ca?.tilesetUrlLocal,
    );
    const newHidden = [...initialTilesets].filter(
      ts => !slideCAUrls.includes(ts.localPath),
    );
    setHiddenTilesets(newHidden);
    if (viewer) {
      viewer.entities.removeAll();
      currentSlide.placements.forEach(p => {
        const so = placementToSO(p);
        if (so) {
          addEntity(viewer, true, showLabels)(so);
        }
      });
    }
  }, [currentSlideId, initialTilesets, slides, viewer, showLabels]);

  useEffect(() => {
    if (infoBoxHandler) {
      infoBoxHandler.destroy();
    }
    if (viewer) {
      const handler = enableEntityInfoBox(viewer, entity => {
        console.log('entity', entity);
      });
      setInfoBoxHandler(handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  function changeSlide(i: number) {
    if (slides[i]) {
      setCurrentSlideId(slides[i].id);
    }
  }

  const currentSlide = slides.find(s => s.id === currentSlideId);
  const currentSlideIndex = slides.findIndex(s => s.id === currentSlideId);

  return (
    <>
      <div className="grid lg:grid-cols-4 gap-6">
        <div className="max-h-screen overflow-auto">
          <SlideSidebar initialTilesets={initialTilesets} initialCesiumAssets={initialCesiumAssets} outcrops={outcrops} />
        </div>
        <div className="lg:col-span-3">
          <CesiumViewer
            initialTilesets={initialTilesets}
            hideTilesets={hiddenTilesets}
            sendCesiumViewer={setViewer}
            showGlobe={true}
            terrainProvider={TerrainProvider.World}
            defaultZoom={false}
            infoBox={true}
          >
            <div className="cesium-vft-info">
              <div className="flex justify-between items-center">
                <Button
                  color="ghost"
                  onClick={() => {
                    changeSlide(currentSlideIndex - 1);
                  }}
                  disabled={currentSlideIndex === 0}
                >
                  <FontAwesomeIcon color="white" size="xl" icon={faCaretLeft} />
                </Button>
                {currentSlide && (
                  <div className="flex flex-grow flex-col">
                    <div className="text-white center flex-1">
                      {currentSlide.text || ''}
                    </div>
                    <div className="text-white center">
                      {currentSlideIndex + 1} / {slides.length}
                    </div>
                  </div>
                )}
                <Button
                  color="ghost"
                  onClick={() => {
                    changeSlide(currentSlideIndex + 1);
                  }}
                  disabled={currentSlideIndex === slides.length - 1}
                >
                  <FontAwesomeIcon
                    color="white"
                    size="xl"
                    icon={faCaretRight}
                  />
                </Button>
              </div>
            </div>
          </CesiumViewer>
          <div>
            <Checkbox
              label={'Show Labels'}
              checked={showLabels}
              onChange={() => {
                setShowLabels(!showLabels);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
